import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useLabProgress } from '../../hooks/useLabProgress';

import {
  ArrowLeft, Star, Check, Lock, ChevronRight, RotateCcw,
  Info, TrendingUp, TrendingDown, DollarSign, BarChart3,
  ArrowUpRight, ArrowDownRight, ShoppingCart, Wallet, Play
} from 'lucide-react';

const STOCKS = [
  { symbol: 'TECH', name: 'TechCorp', basePrice: 150, volatility: 0.08, color: '#3b82f6' },
  { symbol: 'FOOD', name: 'FoodChain Inc', basePrice: 45, volatility: 0.04, color: '#22c55e' },
  { symbol: 'OIL', name: 'PetroEnergy', basePrice: 80, volatility: 0.12, color: '#f59e0b' },
  { symbol: 'BANK', name: 'MegaBank', basePrice: 60, volatility: 0.06, color: '#8b5cf6' },
  { symbol: 'MED', name: 'HealthPharm', basePrice: 200, volatility: 0.05, color: '#ef4444' },
];

const MISSIONS = [
  { id: 1, title: 'Budget Basics', difficulty: 1, type: 'budget',
    description: 'Allocate your monthly income of $3000 across Needs, Wants, Savings, and Investments. Follow the 50/30/20 rule!',
    fact: 'The 50/30/20 rule: 50% for needs (rent, food), 30% for wants (entertainment), 20% for savings/investments. Budgeting is the foundation of financial health!' },
  { id: 2, title: 'Compound Interest', difficulty: 1, type: 'compound',
    description: 'Deposit money in a savings account and watch it grow! Adjust rate and time to see the magic of compound interest.',
    fact: 'Einstein allegedly called compound interest "the eighth wonder of the world." At 7% interest, your money doubles every ~10 years (Rule of 72)!' },
  { id: 3, title: 'Stock Market 101', difficulty: 2, type: 'stocks',
    description: 'Buy and sell stocks to make a profit! Start with $5000 and try to reach $6000.',
    fact: 'The stock market has returned an average of ~10% per year over the last century. However, in any single year, returns can range from -40% to +50%!' },
  { id: 4, title: 'Supply & Demand', difficulty: 2, type: 'supply_demand',
    description: 'Adjust the price of lemonade. Too high = few buyers. Too low = no profit. Find the equilibrium!',
    fact: 'Adam Smith described supply and demand as the "invisible hand" of the market. When supply equals demand, you have market equilibrium!' },
  { id: 5, title: 'Inflation Explorer', difficulty: 2, type: 'inflation',
    description: 'See how inflation erodes purchasing power over time. A $100 item in 1970 costs $800 today!',
    fact: 'Zimbabwe experienced hyperinflation of 79.6 BILLION % per month in 2008. People needed wheelbarrows of cash to buy bread!' },
  { id: 6, title: 'Tax Calculator', difficulty: 1, type: 'tax',
    description: 'Understand progressive tax brackets. Higher income = higher tax rate on the additional amount!',
    fact: 'India tax system is progressive: 0% up to 3L, 5% (3-7L), 10% (7-10L), 15% (10-12L), 20% (12-15L), 30% (>15L). This ensures fairness!' },
  { id: 7, title: 'Startup Simulator', difficulty: 3, type: 'startup',
    description: 'Launch a startup! Allocate budget to Product, Marketing, and Hiring. Get users and revenue!',
    fact: '90% of startups fail. The top reasons: no market need (42%), ran out of cash (29%), wrong team (23%). Product-market fit is everything!' },
  { id: 8, title: 'Import & Export', difficulty: 2, type: 'trade',
    description: 'Trade goods between countries. Buy where it is cheap, sell where it is expensive!',
    fact: 'International trade accounts for 60% of global GDP. Comparative advantage explains why countries specialize: India exports IT, Japan exports cars!' },
  { id: 9, title: 'Banking System', difficulty: 2, type: 'banking',
    description: 'Run a bank! Accept deposits, give loans, and maintain the reserve ratio. Do not run out of cash!',
    fact: 'Banks keep only ~10% of deposits as reserves and lend the rest. This "fractional reserve banking" multiplies money: $100 deposit creates ~$1000 in the economy!' },
  { id: 10, title: 'GDP Dashboard', difficulty: 3, type: 'gdp',
    description: 'Balance government spending, taxes, and exports to maximize GDP growth. Too much spending = inflation!',
    fact: 'GDP = C + I + G + (X-M): Consumption + Investment + Government + Net Exports. India GDP crossed $3.5 trillion in 2023, making it the 5th largest economy!' },
];

function generatePriceHistory(stock, days) {
  const prices = [stock.basePrice];
  for (let i = 1; i < days; i++) {
    const change = (Math.random() - 0.48) * stock.volatility * prices[i - 1];
    prices.push(Math.max(5, prices[i - 1] + change));
  }
  return prices;
}

export default function FinanceSimLab() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [showMissions, setShowMissions] = useState(true);
  const { completedMissions, unlockedMissions, currentMission, setCurrentMission, completeMission: saveProgress, xp, loading: progressLoading } = useLabProgress('finance-sim', 10);
  const [score, setScore] = useState(0);
  const [showFact, setShowFact] = useState(false);

  // Budget
  const [budgetAlloc, setBudgetAlloc] = useState({ needs: 50, wants: 30, savings: 10, invest: 10 });
  // Compound
  const [principal, setPrincipal] = useState(1000);
  const [rate, setRate] = useState(7);
  const [years, setYears] = useState(10);
  // Stocks
  const [cash, setCash] = useState(5000);
  const [portfolio, setPortfolio] = useState({});
  const [priceHistories, setPriceHistories] = useState({});
  const [marketDay, setMarketDay] = useState(0);
  const [marketRunning, setMarketRunning] = useState(false);
  // Supply/Demand
  const [lemonPrice, setLemonPrice] = useState(3);
  // Inflation
  const [inflationRate, setInflationRate] = useState(6);
  const [inflationYears, setInflationYears] = useState(20);
  // Tax
  const [taxIncome, setTaxIncome] = useState(800000);
  // Startup
  const [startupAlloc, setStartupAlloc] = useState({ product: 40, marketing: 30, hiring: 30 });
  const [startupRun, setStartupRun] = useState(false);
  const [startupResult, setStartupResult] = useState(null);
  // Trade
  const [tradeGoods, setTradeGoods] = useState({ wheat: 0, electronics: 0, textiles: 0 });
  const [tradeCash, setTradeCash] = useState(10000);
  // Banking
  const [deposits, setDeposits] = useState(10000);
  const [loans, setLoans] = useState(0);
  const [reserves, setReserves] = useState(10000);
  // GDP
  const [gdpSliders, setGdpSliders] = useState({ govSpend: 30, taxRate: 25, exports: 20 });

  const mission = MISSIONS[currentMission];

  useEffect(() => {
    // Reset states on mission change
    setBudgetAlloc({ needs: 50, wants: 30, savings: 10, invest: 10 });
    setPrincipal(1000); setRate(7); setYears(10);
    setCash(5000); setPortfolio({}); setMarketDay(0); setMarketRunning(false);
    const histories = {};
    STOCKS.forEach(s => { histories[s.symbol] = generatePriceHistory(s, 60); });
    setPriceHistories(histories);
    setLemonPrice(3); setInflationRate(6); setInflationYears(20);
    setTaxIncome(800000);
    setStartupAlloc({ product: 40, marketing: 30, hiring: 30 }); setStartupRun(false); setStartupResult(null);
    setTradeGoods({ wheat: 0, electronics: 0, textiles: 0 }); setTradeCash(10000);
    setDeposits(10000); setLoans(0); setReserves(10000);
    setGdpSliders({ govSpend: 30, taxRate: 25, exports: 20 });
    setShowFact(false);
  }, [currentMission]);

  // Market ticker
  useEffect(() => {
    if (mission.type === 'stocks' && marketRunning && marketDay < 59) {
      const timer = setTimeout(() => setMarketDay(d => d + 1), 500);
      return () => clearTimeout(timer);
    }
  }, [mission.type, marketRunning, marketDay]);

  // Draw stock chart
  const drawChart = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || mission.type !== 'stocks') return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width, h = canvas.height;
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = '#0d0d14'; ctx.fillRect(0, 0, w, h);
    // Grid
    ctx.strokeStyle = 'rgba(255,255,255,0.05)'; ctx.lineWidth = 1;
    for (let i = 0; i < 5; i++) { const y = (h / 5) * i; ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke(); }
    // Plot each stock
    STOCKS.forEach(stock => {
      const prices = priceHistories[stock.symbol];
      if (!prices) return;
      const slice = prices.slice(0, marketDay + 1);
      const allPrices = Object.values(priceHistories).flat();
      const minP = Math.min(...allPrices) * 0.9;
      const maxP = Math.max(...allPrices) * 1.1;
      ctx.strokeStyle = stock.color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      slice.forEach((p, i) => {
        const x = (i / 59) * w;
        const y = h - ((p - minP) / (maxP - minP)) * h;
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      });
      ctx.stroke();
      // Label
      const lastP = slice[slice.length - 1];
      const ly = h - ((lastP - minP) / (maxP - minP)) * h;
      ctx.fillStyle = stock.color;
      ctx.font = '10px sans-serif';
      ctx.fillText(`${stock.symbol} $${lastP.toFixed(0)}`, (marketDay / 59) * w + 5, ly);
    });
  }, [priceHistories, marketDay, mission.type]);

  useEffect(() => { drawChart(); }, [drawChart]);

  const getCurrentPrice = (symbol) => {
    const prices = priceHistories[symbol];
    return prices ? prices[Math.min(marketDay, prices.length - 1)] : 0;
  };

  const buyStock = (symbol) => {
    const price = getCurrentPrice(symbol);
    if (cash >= price) {
      setCash(c => c - price);
      setPortfolio(p => ({ ...p, [symbol]: (p[symbol] || 0) + 1 }));
    }
  };

  const sellStock = (symbol) => {
    if ((portfolio[symbol] || 0) > 0) {
      setCash(c => c + getCurrentPrice(symbol));
      setPortfolio(p => ({ ...p, [symbol]: p[symbol] - 1 }));
    }
  };

  const portfolioValue = Object.entries(portfolio).reduce((sum, [sym, qty]) => sum + qty * getCurrentPrice(sym), 0);
  const totalWealth = cash + portfolioValue;

  const lemonDemand = Math.max(0, Math.round(100 - lemonPrice * 15 + Math.random() * 10));
  const lemonRevenue = lemonDemand * lemonPrice;
  const lemonCost = lemonDemand * 1.5;
  const lemonProfit = lemonRevenue - lemonCost;

  const futureValue = principal * Math.pow(1 + rate / 100, years);
  const interestEarned = futureValue - principal;

  const inflationFuturePrice = 100 * Math.pow(1 + inflationRate / 100, inflationYears);
  const purchasingPower = 100 / Math.pow(1 + inflationRate / 100, inflationYears);

  // Tax brackets (simplified Indian)
  const calcTax = (inc) => {
    const brackets = [
      { limit: 300000, rate: 0 }, { limit: 700000, rate: 5 }, { limit: 1000000, rate: 10 },
      { limit: 1200000, rate: 15 }, { limit: 1500000, rate: 20 }, { limit: Infinity, rate: 30 },
    ];
    let tax = 0, prev = 0;
    for (const b of brackets) {
      if (inc <= prev) break;
      const taxable = Math.min(inc, b.limit) - prev;
      tax += taxable * b.rate / 100;
      prev = b.limit;
    }
    return tax;
  };

  const taxAmount = calcTax(taxIncome);
  const effectiveRate = taxIncome > 0 ? ((taxAmount / taxIncome) * 100).toFixed(1) : 0;

  const runStartup = () => {
    setStartupRun(true);
    const productScore = startupAlloc.product * 2;
    const marketScore = startupAlloc.marketing * 1.5;
    const teamScore = startupAlloc.hiring * 1;
    const total = productScore + marketScore + teamScore;
    const success = total > 300;
    const users = Math.round(marketScore * 100);
    const revenue = Math.round(productScore * teamScore * 0.5);
    setStartupResult({ success, users, revenue, total });
  };

  const reserveRatio = deposits > 0 ? ((reserves / deposits) * 100).toFixed(0) : 0;
  const canLend = reserves - deposits * 0.1;

  const gdpGrowth = (gdpSliders.govSpend * 0.3 + gdpSliders.exports * 0.5 - gdpSliders.taxRate * 0.2 + 2).toFixed(1);
  const gdpInflation = Math.max(0, gdpSliders.govSpend * 0.15 - gdpSliders.taxRate * 0.1).toFixed(1);

  const checkGoal = () => {
    switch (mission.type) {
      case 'budget': return Math.abs(budgetAlloc.needs + budgetAlloc.wants + budgetAlloc.savings + budgetAlloc.invest - 100) < 2 && budgetAlloc.savings + budgetAlloc.invest >= 18;
      case 'compound': return futureValue > 2000;
      case 'stocks': return totalWealth >= 6000;
      case 'supply_demand': return lemonProfit > 30 && lemonProfit < 80;
      case 'inflation': return true; // Observation mission
      case 'tax': return true; // Observation mission
      case 'startup': return startupResult?.success;
      case 'trade': return tradeCash > 12000;
      case 'banking': return loans > 5000 && parseInt(reserveRatio) >= 10;
      case 'gdp': return parseFloat(gdpGrowth) > 5 && parseFloat(gdpInflation) < 5;
      default: return false;
    }
  };

  const completeMission = () => {
    if (!completedMissions.includes(currentMission)) {
      saveProgress(currentMission);
      setScore(s => s + mission.difficulty * 10);
    }
    setShowFact(true);
  };

  const goalMet = checkGoal();
  const isCompleted = completedMissions.includes(currentMission);

  if (showMissions) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border px-4 py-3">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <button onClick={() => navigate('/labs')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <h1 className="text-lg font-bold flex items-center gap-2"><DollarSign className="w-5 h-5 text-emerald-400" /> Finance Simulator</h1>
            <Badge variant="secondary" className="bg-emerald-500/20 text-emerald-300">{score} pts</Badge>
          </div>
        </header>
        <main className="max-w-6xl mx-auto p-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-1">Choose a Mission</h2>
            <p className="text-sm text-muted-foreground">Learn money management, investing, and economics!</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="mission-grid">
            {MISSIONS.map((m, idx) => {
              const locked = !unlockedMissions.includes(idx);
              const done = completedMissions.includes(idx);
              return (
                <motion.button key={m.id} whileHover={!locked ? { scale: 1.04 } : {}} whileTap={!locked ? { scale: 0.97 } : {}}
                  onClick={() => { if (!locked) { setCurrentMission(idx); setShowMissions(false); } }}
                  disabled={locked}
                  className={`relative p-4 rounded-2xl border text-left transition-all ${
                    done ? 'border-emerald-500/30 bg-emerald-500/5' :
                    locked ? 'border-border bg-card opacity-70 cursor-not-allowed' :
                    'border-border bg-card hover:border-emerald-500/30 hover:bg-emerald-500/5'
                  }`}
                  data-testid={`mission-${idx}`}>
                  {locked && <Lock className="absolute top-3 right-3 w-3 h-3 text-muted-foreground/30" />}
                  {done && <Check className="absolute top-3 right-3 w-4 h-4 text-emerald-400" />}
                  <span className="text-[10px] text-foreground uppercase tracking-widest">Mission {idx + 1}</span>
                  <p className="text-sm font-semibold mt-1 text-foreground leading-tight">{m.title}</p>
                  <div className="flex items-center gap-0.5 mt-2">
                    {[...Array(3)].map((_, i) => <Star key={i} className={`w-3 h-3 ${i < m.difficulty ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground/20'}`} />)}
                  </div>
                </motion.button>
              );
            })}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button onClick={() => setShowMissions(true)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition" data-testid="back-to-missions">
            <ArrowLeft className="w-4 h-4" /> Missions
          </button>
          <h1 className="text-sm font-semibold text-foreground/80">{mission.title}</h1>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground/70">{score} pts</span>
            <span className="text-xs text-muted-foreground/70">{completedMissions.length}/10</span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4" data-testid="finance-bench">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
          <div className="space-y-4">
            {/* Budget */}
            {mission.type === 'budget' && (
              <div className="p-5 rounded-2xl border border-border bg-card space-y-4" data-testid="budget-area">
                <p className="text-sm text-muted-foreground">Monthly Income: <span className="text-emerald-400 font-bold">$3,000</span></p>
                {Object.entries(budgetAlloc).map(([key, val]) => (
                  <div key={key}>
                    <label className="text-xs text-muted-foreground flex justify-between mb-1">
                      <span className="capitalize">{key}</span>
                      <span className="text-foreground font-mono">{val}% (${(3000 * val / 100).toFixed(0)})</span>
                    </label>
                    <input type="range" min={0} max={100} value={val}
                      onChange={e => setBudgetAlloc(p => ({ ...p, [key]: +e.target.value }))}
                      className="w-full accent-emerald-500" data-testid={`slider-${key}`} />
                  </div>
                ))}
                <div className={`text-xs font-semibold p-2 rounded-lg ${
                  Math.abs(budgetAlloc.needs + budgetAlloc.wants + budgetAlloc.savings + budgetAlloc.invest - 100) < 2
                    ? 'text-emerald-400 bg-emerald-500/10' : 'text-red-400 bg-red-500/10'
                }`}>
                  Total: {budgetAlloc.needs + budgetAlloc.wants + budgetAlloc.savings + budgetAlloc.invest}% (target: 100%)
                </div>
              </div>
            )}

            {/* Compound Interest */}
            {mission.type === 'compound' && (
              <div className="p-5 rounded-2xl border border-border bg-card space-y-4" data-testid="compound-area">
                <div><label className="text-xs text-muted-foreground flex justify-between mb-1"><span>Principal ($)</span><span className="font-mono text-foreground">${principal}</span></label>
                  <input type="range" min={100} max={10000} step={100} value={principal} onChange={e => setPrincipal(+e.target.value)} className="w-full accent-emerald-500" data-testid="slider-principal" /></div>
                <div><label className="text-xs text-muted-foreground flex justify-between mb-1"><span>Interest Rate (%)</span><span className="font-mono text-foreground">{rate}%</span></label>
                  <input type="range" min={1} max={20} step={0.5} value={rate} onChange={e => setRate(+e.target.value)} className="w-full accent-emerald-500" data-testid="slider-rate" /></div>
                <div><label className="text-xs text-muted-foreground flex justify-between mb-1"><span>Years</span><span className="font-mono text-foreground">{years}y</span></label>
                  <input type="range" min={1} max={40} value={years} onChange={e => setYears(+e.target.value)} className="w-full accent-emerald-500" data-testid="slider-years" /></div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-3 rounded-xl bg-muted/50"><p className="text-[10px] text-muted-foreground/70">Principal</p><p className="font-bold text-white">${principal.toLocaleString()}</p></div>
                  <div className="p-3 rounded-xl bg-emerald-500/10"><p className="text-[10px] text-emerald-400">Interest Earned</p><p className="font-bold text-emerald-300">${interestEarned.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p></div>
                  <div className="p-3 rounded-xl bg-violet-500/10"><p className="text-[10px] text-violet-400">Future Value</p><p className="font-bold text-violet-300">${futureValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p></div>
                </div>
              </div>
            )}

            {/* Stocks */}
            {mission.type === 'stocks' && (
              <div className="space-y-3" data-testid="stocks-area">
                <div className="rounded-2xl border border-border bg-card p-2">
                  <canvas ref={canvasRef} width={600} height={280} className="w-full rounded-xl" data-testid="stock-chart" />
                </div>
                <div className="flex items-center gap-3">
                  <Button onClick={() => setMarketRunning(!marketRunning)} className={marketRunning ? 'bg-red-600' : 'bg-emerald-600'} data-testid="market-toggle">
                    <Play className="w-4 h-4 mr-1" /> {marketRunning ? 'Pause' : 'Start'} Market
                  </Button>
                  <span className="text-xs text-muted-foreground/70">Day {marketDay + 1}/60</span>
                  <span className="text-xs text-muted-foreground/70">Cash: <span className="text-emerald-400">${cash.toFixed(0)}</span></span>
                  <span className="text-xs text-muted-foreground/70">Portfolio: <span className="text-violet-400">${portfolioValue.toFixed(0)}</span></span>
                  <span className="text-xs font-bold" style={{ color: totalWealth >= 6000 ? '#10b981' : '#fff' }}>Total: ${totalWealth.toFixed(0)}</span>
                </div>
                <div className="grid grid-cols-5 gap-2">
                  {STOCKS.map(s => {
                    const price = getCurrentPrice(s.symbol);
                    const qty = portfolio[s.symbol] || 0;
                    return (
                      <div key={s.symbol} className="p-2 rounded-xl border border-border bg-card text-center">
                        <p className="text-[10px] font-bold" style={{ color: s.color }}>{s.symbol}</p>
                        <p className="text-xs text-foreground/80">${price.toFixed(0)}</p>
                        <p className="text-[10px] text-muted-foreground/70">Own: {qty}</p>
                        <div className="flex gap-1 mt-1">
                          <button onClick={() => buyStock(s.symbol)} className="flex-1 text-[10px] py-0.5 rounded bg-emerald-600 hover:bg-emerald-500" data-testid={`buy-${s.symbol}`}>Buy</button>
                          <button onClick={() => sellStock(s.symbol)} className="flex-1 text-[10px] py-0.5 rounded bg-red-600 hover:bg-red-500" data-testid={`sell-${s.symbol}`}>Sell</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Supply & Demand */}
            {mission.type === 'supply_demand' && (
              <div className="p-5 rounded-2xl border border-border bg-card space-y-4" data-testid="supply-area">
                <p className="text-sm text-muted-foreground">Set your lemonade price. Cost per cup: $1.50</p>
                <div><label className="text-xs text-muted-foreground flex justify-between mb-1"><span>Price per Cup</span><span className="font-mono text-foreground">${lemonPrice.toFixed(1)}</span></label>
                  <input type="range" min={0.5} max={8} step={0.5} value={lemonPrice} onChange={e => setLemonPrice(+e.target.value)} className="w-full accent-amber-500" data-testid="slider-price" /></div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-3 rounded-xl bg-blue-500/10"><p className="text-[10px] text-blue-400">Demand</p><p className="font-bold text-blue-300">{lemonDemand} cups</p></div>
                  <div className="p-3 rounded-xl bg-amber-500/10"><p className="text-[10px] text-amber-400">Revenue</p><p className="font-bold text-amber-300">${lemonRevenue.toFixed(0)}</p></div>
                  <div className={`p-3 rounded-xl ${lemonProfit > 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}><p className="text-[10px] text-muted-foreground/70">Profit</p><p className={`font-bold ${lemonProfit > 0 ? 'text-emerald-300' : 'text-red-300'}`}>${lemonProfit.toFixed(0)}</p></div>
                </div>
              </div>
            )}

            {/* Inflation */}
            {mission.type === 'inflation' && (
              <div className="p-5 rounded-2xl border border-border bg-card space-y-4" data-testid="inflation-area">
                <div><label className="text-xs text-muted-foreground flex justify-between mb-1"><span>Inflation Rate</span><span className="font-mono text-foreground">{inflationRate}%</span></label>
                  <input type="range" min={1} max={15} value={inflationRate} onChange={e => setInflationRate(+e.target.value)} className="w-full accent-red-500" data-testid="slider-inflation" /></div>
                <div><label className="text-xs text-muted-foreground flex justify-between mb-1"><span>Years Forward</span><span className="font-mono text-foreground">{inflationYears}y</span></label>
                  <input type="range" min={1} max={50} value={inflationYears} onChange={e => setInflationYears(+e.target.value)} className="w-full accent-red-500" data-testid="slider-inf-years" /></div>
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="p-3 rounded-xl bg-red-500/10"><p className="text-[10px] text-red-400">$100 item in {inflationYears}y</p><p className="font-bold text-red-300">${inflationFuturePrice.toFixed(0)}</p></div>
                  <div className="p-3 rounded-xl bg-blue-500/10"><p className="text-[10px] text-blue-400">$100 buying power</p><p className="font-bold text-blue-300">${purchasingPower.toFixed(0)}</p></div>
                </div>
              </div>
            )}

            {/* Tax */}
            {mission.type === 'tax' && (
              <div className="p-5 rounded-2xl border border-border bg-card space-y-4" data-testid="tax-area">
                <div><label className="text-xs text-muted-foreground flex justify-between mb-1"><span>Annual Income</span><span className="font-mono text-foreground">Rs {taxIncome.toLocaleString()}</span></label>
                  <input type="range" min={100000} max={3000000} step={50000} value={taxIncome} onChange={e => setTaxIncome(+e.target.value)} className="w-full accent-violet-500" data-testid="slider-income" /></div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-3 rounded-xl bg-muted/50"><p className="text-[10px] text-muted-foreground/70">Gross Income</p><p className="font-bold">Rs {taxIncome.toLocaleString()}</p></div>
                  <div className="p-3 rounded-xl bg-red-500/10"><p className="text-[10px] text-red-400">Tax</p><p className="font-bold text-red-300">Rs {taxAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p></div>
                  <div className="p-3 rounded-xl bg-emerald-500/10"><p className="text-[10px] text-emerald-400">Effective Rate</p><p className="font-bold text-emerald-300">{effectiveRate}%</p></div>
                </div>
              </div>
            )}

            {/* Startup */}
            {mission.type === 'startup' && (
              <div className="p-5 rounded-2xl border border-border bg-card space-y-4" data-testid="startup-area">
                <p className="text-sm text-muted-foreground">Budget: $100K. Allocate across Product, Marketing, Hiring.</p>
                {Object.entries(startupAlloc).map(([key, val]) => (
                  <div key={key}><label className="text-xs text-muted-foreground flex justify-between mb-1"><span className="capitalize">{key}</span><span className="font-mono text-foreground">{val}% (${val}K)</span></label>
                    <input type="range" min={5} max={70} value={val} onChange={e => setStartupAlloc(p => ({ ...p, [key]: +e.target.value }))} className="w-full accent-violet-500" data-testid={`slider-${key}`} /></div>
                ))}
                <Button onClick={runStartup} disabled={startupRun} className="bg-violet-600" data-testid="launch-startup"><Play className="w-4 h-4 mr-1" /> Launch Startup!</Button>
                {startupResult && (
                  <div className={`p-3 rounded-xl ${startupResult.success ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
                    <p className="font-bold text-sm">{startupResult.success ? 'Startup Succeeded!' : 'Startup Failed'}</p>
                    <p className="text-xs text-muted-foreground mt-1">Users: {startupResult.users.toLocaleString()} | Revenue: ${startupResult.revenue.toLocaleString()}</p>
                  </div>
                )}
              </div>
            )}

            {/* Trade */}
            {mission.type === 'trade' && (
              <div className="p-5 rounded-2xl border border-border bg-card space-y-4" data-testid="trade-area">
                <p className="text-sm text-muted-foreground">Cash: <span className="text-emerald-400 font-bold">${tradeCash.toLocaleString()}</span> — Buy low, sell high!</p>
                {[
                  { name: 'Wheat', key: 'wheat', buyPrice: 500, sellPrice: 800 },
                  { name: 'Electronics', key: 'electronics', buyPrice: 2000, sellPrice: 3200 },
                  { name: 'Textiles', key: 'textiles', buyPrice: 300, sellPrice: 550 },
                ].map(g => (
                  <div key={g.key} className="flex items-center gap-3 p-2 rounded-xl bg-muted/50">
                    <div className="flex-1"><p className="text-xs font-semibold">{g.name}</p><p className="text-[10px] text-muted-foreground/70">Buy: ${g.buyPrice} | Sell: ${g.sellPrice}</p></div>
                    <span className="text-xs text-muted-foreground min-w-[40px] text-center">{tradeGoods[g.key]}</span>
                    <button onClick={() => { if (tradeCash >= g.buyPrice) { setTradeCash(c => c - g.buyPrice); setTradeGoods(p => ({ ...p, [g.key]: p[g.key] + 1 })); } }}
                      className="text-[10px] px-2 py-1 rounded bg-blue-600" data-testid={`buy-${g.key}`}>Buy</button>
                    <button onClick={() => { if (tradeGoods[g.key] > 0) { setTradeCash(c => c + g.sellPrice); setTradeGoods(p => ({ ...p, [g.key]: p[g.key] - 1 })); } }}
                      className="text-[10px] px-2 py-1 rounded bg-amber-600" data-testid={`sell-${g.key}`}>Sell</button>
                  </div>
                ))}
              </div>
            )}

            {/* Banking */}
            {mission.type === 'banking' && (
              <div className="p-5 rounded-2xl border border-border bg-card space-y-4" data-testid="banking-area">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-3 rounded-xl bg-blue-500/10"><p className="text-[10px] text-blue-400">Deposits</p><p className="font-bold text-blue-300">${deposits.toLocaleString()}</p></div>
                  <div className="p-3 rounded-xl bg-amber-500/10"><p className="text-[10px] text-amber-400">Loans Given</p><p className="font-bold text-amber-300">${loans.toLocaleString()}</p></div>
                  <div className={`p-3 rounded-xl ${parseInt(reserveRatio) >= 10 ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}><p className="text-[10px] text-muted-foreground/70">Reserve Ratio</p><p className="font-bold">{reserveRatio}%</p></div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => { setDeposits(d => d + 1000); setReserves(r => r + 1000); }} className="bg-blue-600" data-testid="accept-deposit">Accept $1K Deposit</Button>
                  <Button size="sm" onClick={() => { if (canLend >= 1000) { setLoans(l => l + 1000); setReserves(r => r - 1000); } }}
                    disabled={canLend < 1000} className="bg-amber-600" data-testid="give-loan">Give $1K Loan</Button>
                </div>
                <p className="text-xs text-muted-foreground/60">Maintain at least 10% reserves. Lendable: ${Math.max(0, canLend).toLocaleString()}</p>
              </div>
            )}

            {/* GDP */}
            {mission.type === 'gdp' && (
              <div className="p-5 rounded-2xl border border-border bg-card space-y-4" data-testid="gdp-area">
                {[
                  { key: 'govSpend', label: 'Government Spending %' },
                  { key: 'taxRate', label: 'Tax Rate %' },
                  { key: 'exports', label: 'Export Focus %' },
                ].map(s => (
                  <div key={s.key}><label className="text-xs text-muted-foreground flex justify-between mb-1"><span>{s.label}</span><span className="font-mono text-foreground">{gdpSliders[s.key]}%</span></label>
                    <input type="range" min={5} max={60} value={gdpSliders[s.key]} onChange={e => setGdpSliders(p => ({ ...p, [s.key]: +e.target.value }))} className="w-full accent-emerald-500" data-testid={`slider-${s.key}`} /></div>
                ))}
                <div className="grid grid-cols-2 gap-2 text-center">
                  <div className={`p-3 rounded-xl ${parseFloat(gdpGrowth) > 5 ? 'bg-emerald-500/10' : 'bg-muted/50'}`}><p className="text-[10px] text-muted-foreground/70">GDP Growth</p><p className="font-bold text-emerald-300">{gdpGrowth}%</p></div>
                  <div className={`p-3 rounded-xl ${parseFloat(gdpInflation) < 5 ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}><p className="text-[10px] text-muted-foreground/70">Inflation</p><p className={`font-bold ${parseFloat(gdpInflation) < 5 ? 'text-emerald-300' : 'text-red-300'}`}>{gdpInflation}%</p></div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              {goalMet && !isCompleted && (
                <Button onClick={completeMission} className="bg-gradient-to-r from-emerald-500 to-teal-600" data-testid="complete-btn">
                  <Check className="w-4 h-4 mr-1" /> Mission Complete!
                </Button>
              )}
              <Button variant="outline" onClick={() => {
                setBudgetAlloc({ needs: 50, wants: 30, savings: 10, invest: 10 });
                setPrincipal(1000); setRate(7); setYears(10); setCash(5000);
                setPortfolio({}); setMarketDay(0); setMarketRunning(false);
                setLemonPrice(3); setStartupRun(false); setStartupResult(null);
                setTradeCash(10000); setTradeGoods({ wheat: 0, electronics: 0, textiles: 0 });
                setDeposits(10000); setLoans(0); setReserves(10000);
                setGdpSliders({ govSpend: 30, taxRate: 25, exports: 20 });
                setShowFact(false);
              }} className="border-white/20 text-muted-foreground" data-testid="reset-btn">
                <RotateCcw className="w-4 h-4 mr-1" /> Reset
              </Button>
            </div>

            <AnimatePresence>
              {showFact && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="p-4 rounded-2xl border border-emerald-500/20 bg-emerald-500/5" data-testid="finance-fact">
                  <p className="text-xs text-emerald-400 font-semibold flex items-center gap-1 mb-1"><Info className="w-3 h-3" /> Finance Fact</p>
                  <p className="text-sm text-foreground/80">{mission.fact}</p>
                  {currentMission < MISSIONS.length - 1 && (
                    <Button size="sm" onClick={() => { setCurrentMission(p => p + 1); setShowFact(false); }}
                      className="mt-3 bg-emerald-600 hover:bg-emerald-500" data-testid="next-mission-btn">
                      Next Mission <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 lg:sticky lg:top-[65px] lg:self-start">
            <div className="p-4 rounded-2xl border border-border bg-card">
              <h3 className="font-semibold text-sm mb-2 flex items-center gap-1.5"><BarChart3 className="w-4 h-4 text-emerald-400" /> Objective</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">{mission.description}</p>
            </div>
            <div className="p-4 rounded-2xl border border-border bg-card">
              <h3 className="font-semibold text-sm mb-2">Progress</h3>
              <div className="space-y-1.5">
                {MISSIONS.map((m, idx) => (
                  <button key={m.id} onClick={() => { if (unlockedMissions.includes(idx)) { setCurrentMission(idx); setShowMissions(false); setShowFact(false); } }}
                    disabled={!unlockedMissions.includes(idx)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-all ${
                      idx === currentMission ? 'bg-primary text-primary-foreground' :
                      completedMissions.includes(idx) ? 'text-emerald-400 hover:bg-muted/50' :
                      unlockedMissions.includes(idx) ? 'text-muted-foreground hover:bg-muted/50' : 'text-muted-foreground/30 cursor-not-allowed'
                    }`}>
                    <span className={`w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold ${
                      completedMissions.includes(idx) ? 'bg-emerald-500 text-white' :
                      idx === currentMission ? 'bg-primary text-primary-foreground' : 'bg-muted/50'
                    }`}>
                      {completedMissions.includes(idx) ? <Check className="w-3 h-3" /> : unlockedMissions.includes(idx) ? idx + 1 : <Lock className="w-2.5 h-2.5" />}
                    </span>
                    <span className="truncate">{m.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
